import { AlertCircle, Minus, Plus, ShoppingBagIcon, X } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { useCart } from "@/providers/CartProvider";
import { useStore } from "../../context/StoreContext";
import { useBasePath } from "@/providers/BasePathProvider";
import { Button } from "../ui/Button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../ui/sheet";
import Image from "next/image";
import { CartItemPopulated } from "@/lib/Apis";
import { PLACEHOLDER_IMAGE_SRC } from "@/lib/constants/assets";
import { useProduct } from "@/hooks/useProducts";

function CartDrawerItem({ item, removeFromCart, updateQuantity, setCartOpen }: {
  item: CartItemPopulated;
  removeFromCart: (variantId: string) => Promise<void>;
  updateQuantity: (variantId: string, quantity: number) => void;
  setCartOpen: (open: boolean) => void;
}) {
  const [isRemoving, setIsRemoving] = useState(false);
  const { buildPath } = useBasePath();

  const productId =
    (item as any).productId ||
    (item as any).product?._id ||
    (item as any).product?.id ||
    (item.productVariantData as any)?.productId ||
    (item.productVariantData as any)?.product?._id ||
    (item.productVariantData as any)?.product?.id;

  const { product } = useProduct(productId);

  const productName = (item as any).product?.name || product?.name;
  const variantName = item.productVariantData?.name;
  const displayName = productName || variantName;

  const isVariantImagePlaceholder =
    !item.productVariantData?.media?.[0]?.file ||
    item.productVariantData?.media?.[0]?.file === PLACEHOLDER_IMAGE_SRC;

  const displayImage =
    (item as any).product?.media?.[0]?.file ||
    (!isVariantImagePlaceholder ? item.productVariantData?.media?.[0]?.file : null) ||
    product?.media?.[0]?.file ||
    PLACEHOLDER_IMAGE_SRC;

  const price = item.productVariantData?.finalPrice || 0;
  const productHref = buildPath(`/products/${productId}`);

  const handleRemove = async () => {
    setIsRemoving(true);
    try {
      await removeFromCart(item.productVariantId);
    } finally {
      setIsRemoving(false);
    }
  };

  // Grid: [image] [name + qty] [X + price]
  return (
    <div className="grid grid-cols-[80px_1fr_auto] gap-3 items-start">
      {/* Image */}
      <Link
        href={productHref}
        onClick={() => setCartOpen(false)}
        className="w-20 h-20 rounded-2xl bg-gray-100 overflow-hidden hover:opacity-80 transition-opacity"
      >
        <Image
          src={displayImage}
          alt={displayName || "Product"}
          className="w-full h-full object-cover"
          height={80}
          width={80}
        />
      </Link>

      {/* Name + qty */}
      <div className="min-w-0 pt-0.5">
        <Link
          href={productHref}
          onClick={() => setCartOpen(false)}
          className="text-sm font-medium text-gray-900 hover:text-hprimary transition-colors line-clamp-2 leading-snug"
        >
          {displayName}
        </Link>
        {productName && variantName && (
          <p className="text-xs text-gray-500 mt-0.5 truncate">{variantName}</p>
        )}
        <div className="flex items-center gap-2 bg-gray-100 rounded-full px-3 py-1 w-fit mt-2">
          <button
            aria-label="Decrease quantity"
            onClick={() => updateQuantity(item.productVariantId, Math.max(1, item.quantity - 1))}
            className="text-gray-600 hover:text-gray-900"
          >
            <Minus className="w-3 h-3" />
          </button>
          <span className="text-sm font-medium w-4 text-center">{item.quantity}</span>
          <button
            aria-label="Increase quantity"
            onClick={() => updateQuantity(item.productVariantId, item.quantity + 1)}
            className="text-gray-600 hover:text-gray-900"
          >
            <Plus className="w-3 h-3" />
          </button>
        </div>
      </div>

      {/* X + price — dedicated grid column, always visible */}
      <div className="flex flex-col items-end justify-between h-20 pt-0.5 pb-1">
        <button
          aria-label="Remove item"
          onClick={handleRemove}
          disabled={isRemoving}
          className="text-gray-400 hover:text-red-500 disabled:opacity-50 transition-colors"
        >
          {isRemoving
            ? <span className="inline-block w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
            : <X className="w-4 h-4" />
          }
        </button>
        <span className="text-sm font-medium text-gray-900">${(price * item.quantity).toFixed(2)}</span>
      </div>
    </div>
  );
}

function CartItemSkeleton() {
  return (
    <div className="grid grid-cols-[80px_1fr_auto] gap-3 items-start animate-pulse">
      <div className="w-20 h-20 rounded-2xl bg-gray-200" />
      <div className="pt-0.5 space-y-2">
        <div className="h-4 bg-gray-200 rounded w-3/4" />
        <div className="h-3 bg-gray-200 rounded w-1/2" />
        <div className="h-8 bg-gray-200 rounded-full w-24 mt-2" />
      </div>
      <div className="flex flex-col items-end justify-between h-20 pt-0.5 pb-1">
        <div className="w-4 h-4 bg-gray-200 rounded" />
        <div className="h-4 bg-gray-200 rounded w-12" />
      </div>
    </div>
  );
}

export function CartDrawer() {
  const { cart, removeFromCart, updateQuantity, isFetching, cartError, clearCartError } = useCart();
  const { isCartOpen, setCartOpen } = useStore();
  const { buildPath } = useBasePath();
  const router = useRouter();
  const [showBottomFade, setShowBottomFade] = useState(true);

  function handleScroll(e: React.UIEvent<HTMLDivElement>) {
    const el = e.currentTarget;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 8;
    setShowBottomFade(!atBottom);
  }

  const items = cart?.cartBody?.items || [];
  const cartTotal = cart?.subTotal || 0;

  return (
    <Sheet open={isCartOpen} onOpenChange={setCartOpen}>
      <SheetContent side="right" className="flex flex-col p-0 w-[90vw] sm:max-w-md h-full overflow-hidden">
        {/* Header */}
        <SheetHeader className="flex-row items-center justify-between gap-2 border-b border-gray-200 px-6 py-4 space-y-0">
          <div className="flex items-center gap-2">
            <ShoppingBagIcon className="w-5 h-5 text-gray-700" />
            <SheetTitle className="text-xl font-medium text-hsecondary">Your Cart</SheetTitle>
            <p className="text-sm text-gray-500">({items.length} item{items.length !== 1 ? "s" : ""})</p>
          </div>
          <SheetDescription className="sr-only">Shopping cart contents</SheetDescription>
        </SheetHeader>

        {cartError && (
          <div className="flex items-start gap-3 mx-4 mt-4 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0 text-red-500" />
            <span className="flex-1">{cartError}</span>
            <button onClick={clearCartError} className="shrink-0 text-red-400 hover:text-red-600">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* {isFetching && items.length === 0 ? (
          <div className="relative flex-1 min-h-0">
            <div className="h-full overflow-y-auto px-6">
              <div className="py-4 space-y-6">
                {[...Array(3)].map((_, i) => <CartItemSkeleton key={i} />)}
              </div>
            </div>
          </div>
        )} */}

        {isFetching && items.length === 0 ? (
          <div className="relative flex-1 min-h-0">
            <div className="h-full overflow-y-auto px-6">
              <div className="py-4 space-y-6">
                {[...Array(3)].map((_, i) => <CartItemSkeleton key={i} />)}
              </div>
            </div>
          </div>
        ) : items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
              <Plus className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Your cart is empty</h3>
            <p className="text-sm text-gray-500 text-center mb-6">
              Add items to your cart to get started
            </p>
            <Button
              className="bg-hsecondary text-white hover:bg-hsecondary/90 rounded-full"
              onClick={() => { setCartOpen(false); router.push(buildPath('/shop')); }}
            >
              Continue Shopping
            </Button>
          </div>
        ) : (
          <>
            {/* Cart Items */}
            <div className="relative flex-1 min-h-0">
              <div
                onScroll={handleScroll}
                className="h-full overflow-y-auto px-6 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-track]:bg-transparent"
              >
                <div className="py-4 space-y-6">
                  {items.map((item) => (
                    <CartDrawerItem
                      key={item.productVariantId}
                      item={item}
                      removeFromCart={removeFromCart}
                      updateQuantity={updateQuantity}
                      setCartOpen={setCartOpen}
                    />
                  ))}
                </div>
              </div>
              {/* Bottom fade — fades out when scrolled to end */}
              {showBottomFade && (
                <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-16 bg-linear-to-t from-white to-transparent" />
              )}
            </div>

            {/* Summary */}
            <div className="px-6 py-6 border-t border-gray-200 space-y-4 bg-hmuted/5">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal</span>
                  <span>${cartTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Shipping</span>
                  <span>Calculated at checkout</span>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-4 mb-6">
                <div className="flex justify-between items-baseline mb-1">
                  <span className="text-base font-normal text-gray-900">Total</span>
                  <span className="text-2xl font-medium text-gray-900">${cartTotal.toFixed(2)}</span>
                </div>
              </div>

              <Link href={buildPath('/checkout')} className="flex w-full mb-3 mx-auto items-center justify-center">
                <Button
                  className="w-full rounded-full border-2 border-hsecondary bg-hsecondary hover:bg-hsecondary/90 text-white px-4 py-3 text-sm font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => setCartOpen(false)}
                >
                  Proceed to Checkout
                  <span>→</span>
                </Button>
              </Link>

              <p className="text-xs text-center text-gray-500">
                or{" "}
                <Link
                  href={buildPath('/shop')}
                  onClick={() => setCartOpen(false)}
                  className="text-gray-900 hover:underline font-medium"
                >
                  continue shopping
                </Link>
              </p>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
